/**
 * WCAG 2.1 contrast utilities for color accessibility checks.
 */

/** Parse a hex color string to [r, g, b] (0-255). Supports #RGB and #RRGGBB. */
function parseHex(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  let r: number, g: number, b: number;
  if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16);
    g = parseInt(h[1] + h[1], 16);
    b = parseInt(h[2] + h[2], 16);
  } else if (h.length === 6) {
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else {
    return [0, 0, 0];
  }
  // Guard against non-hex characters
  if (isNaN(r) || isNaN(g) || isNaN(b)) return [0, 0, 0];
  return [r, g, b];
}

/** WCAG relative luminance (0–1) from sRGB hex. */
function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG contrast ratio between two hex colors. Returns value between 1 and 21.
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Returns "#000" or "#fff" — whichever has better contrast against the given background.
 */
export function getContrastTextColor(hexBg: string): "#000" | "#fff" {
  const lum = relativeLuminance(hexBg);
  // Compare contrast with black vs white
  const contrastWithWhite = (1 + 0.05) / (lum + 0.05);
  const contrastWithBlack = (lum + 0.05) / (0 + 0.05);
  return contrastWithWhite >= contrastWithBlack ? "#fff" : "#000";
}
